import { useEffect, useRef } from 'react';
import {
  FT_INNER,
  FT_MIDDLE,
  FT_OUTER,
  DEFAULT_ARROW_LENGTH_FT,
  METERS_PER_FOOT,
  computeDestinationFeet,
  smartRotateDeg,
} from './isdBlockGeometry';
import { createIsdLabelOverlayViewClass } from './IsdLabelOverlayView';

const COLOR_INNER = '#DC2626';
const COLOR_MIDDLE = '#2563EB';
const COLOR_OUTER = '#EAB308';
const COLOR_ARROW = '#EAB308';
const COLOR_ACTIVE = '#FFEE00'; // bright safety yellow
const COLOR_ACTIVE_HALO = '#000000';
const STROKE_WEIGHT_MAIN = 2;
const STROKE_WEIGHT_HALO = 4; // main + 1px black on each side
// Inner disc is solid (no opacity). Acts as the drag target on the active
// block — user grabs the disc anywhere to move the whole mark.
const FILL_ALPHA_INNER_INACTIVE = 1;
const FILL_ALPHA_INNER_ACTIVE = 1;
const STROKE_OPACITY_INACTIVE_IDLE = 0.6;
const STROKE_OPACITY_INACTIVE_HOVER = 0.9;
const STROKE_OPACITY_ACTIVE = 1;
// When a lane is selected in the picker, marks on OTHER lanes fade to this so
// the selected lane's marks stand out (spotlight). 0 = no lane selected.
const OPACITY_DIMMED = 0.12;

/**
 * Renders the ISD block (3 circles + arrow + labels + handle dots) for each
 * intersection. Labels and handle dots only render on the active intersection.
 *
 * Returns a ref to the per-intersection overlay bundle so callers (the drawing
 * hook) can attach drag listeners to the handles.
 */
export function useIsdBlockOverlays({
  map,
  intersections,
  activeIntersectionId,
  selectedTargetId = null,
  pathsPanelMode,
  onSetActiveIntersection,
}) {
  const overlaysRef = useRef({});
  const LabelClassRef = useRef(null);
  const activeIdRef = useRef(activeIntersectionId);
  activeIdRef.current = activeIntersectionId;
  const onSetActiveRef = useRef(onSetActiveIntersection);
  onSetActiveRef.current = onSetActiveIntersection;

  useEffect(() => {
    if (!window.google?.maps?.OverlayView) return;
    if (!LabelClassRef.current) {
      LabelClassRef.current = createIsdLabelOverlayViewClass();
    }
  }, [map]);

  useEffect(() => {
    if (!map || !window.google?.maps) return;
    if (pathsPanelMode !== 'intersection') {
      Object.values(overlaysRef.current).forEach((bundle) => removeBundle(bundle));
      overlaysRef.current = {};
      return;
    }

    const g = window.google.maps;
    const LabelClass = LabelClassRef.current ?? createIsdLabelOverlayViewClass();
    LabelClassRef.current = LabelClass;

    const live = new Set();

    intersections.forEach((isd) => {
      if (!isd.visible || !isd.insertionPoint) return;
      live.add(isd.id);
      const isActive = isd.id === activeIntersectionId;
      // Spotlight: when a lane is selected, marks on other lanes fade out.
      // The active mark always stays bright.
      const isDimmed =
        !isActive && !!selectedTargetId && isd.rsaPathId !== selectedTargetId;
      const azimuth = Number.isFinite(isd.azimuth) ? isd.azimuth : 0;
      const arrowLen = Number.isFinite(isd.arrowLengthFeet)
        ? isd.arrowLengthFeet
        : DEFAULT_ARROW_LENGTH_FT;

      let bundle = overlaysRef.current[isd.id];
      if (!bundle) {
        bundle = createBundle(g, map, isd.id, activeIdRef, onSetActiveRef);
        overlaysRef.current[isd.id] = bundle;
      }
      bundle.id = isd.id;
      bundle.isDimmed = isDimmed;

      bundle.innerCircle.setCenter(isd.insertionPoint);
      bundle.innerCircle.setRadius(FT_INNER * METERS_PER_FOOT);
      bundle.innerCircle.setOptions(circleOptsInner(isActive, isDimmed));
      bundle.innerHalo.setCenter(isd.insertionPoint);
      bundle.innerHalo.setRadius(FT_INNER * METERS_PER_FOOT);
      bundle.innerHalo.setOptions(haloOpts(isActive));

      bundle.middleCircle.setCenter(isd.insertionPoint);
      bundle.middleCircle.setRadius(FT_MIDDLE * METERS_PER_FOOT);
      bundle.middleCircle.setOptions(circleOptsMiddle(isActive, isDimmed));
      bundle.middleHalo.setCenter(isd.insertionPoint);
      bundle.middleHalo.setRadius(FT_MIDDLE * METERS_PER_FOOT);
      bundle.middleHalo.setOptions(haloOpts(isActive));

      bundle.outerCircle.setCenter(isd.insertionPoint);
      bundle.outerCircle.setRadius(FT_OUTER * METERS_PER_FOOT);
      bundle.outerCircle.setOptions(circleOptsOuter(isActive, isDimmed));
      bundle.outerHalo.setCenter(isd.insertionPoint);
      bundle.outerHalo.setRadius(FT_OUTER * METERS_PER_FOOT);
      bundle.outerHalo.setOptions(haloOpts(isActive));

      const tip = computeDestinationFeet(isd.insertionPoint, azimuth, arrowLen);
      bundle.arrow.setPath([isd.insertionPoint, tip]);
      bundle.arrow.setOptions(arrowOpts(isActive, isDimmed));
      bundle.arrowHalo.setPath([isd.insertionPoint, tip]);
      bundle.arrowHalo.setOptions(arrowHaloOpts(isActive));

      // Center "handle" is hidden — the inner disc itself is the drag target
      // (see circleOptsInner: clickable + draggable when active). Keeping
      // the marker in the bundle for compatibility but never showing it.
      bundle.centerHandle.setPosition(isd.insertionPoint);
      bundle.centerHandle.setVisible(false);
      bundle.tipHandle.setPosition(tip);
      bundle.tipHandle.setVisible(isActive);

      // Spread labels around the block so they don't overlap on the
      // arrow line. Each label is tangent-rotated at its own position.
      // 6 ft and 14.5 ft sit OUTSIDE their circle strokes (offset radially
      // outward) so the text doesn't overlap the line. 4.0 ft stays at
      // center (inside the red disc, no stroke to overlap).
      const tangentRot = smartRotateDeg(azimuth);
      const perpCcwRot = smartRotateDeg(azimuth - 90);
      const perpCwRot = smartRotateDeg(azimuth + 90);
      const LABEL_OUTSIDE_OFFSET_FT = 1.5;
      // 6 ft and 14.5 ft sit on opposite perpendicular sides of the arrow so
      // they don't cross the azimuth line as it rotates with the mark.
      const labelSpecs = isActive ? [
        // 4.0 ft centered on the solid yellow disc — black text for contrast.
        { pos: isd.insertionPoint, text: '4.0 ft', rotationDeg: tangentRot, color: '#000000' },
        // 6 ft just outside the middle circle, 90° CCW from the arrow.
        { pos: computeDestinationFeet(isd.insertionPoint, azimuth - 90, FT_MIDDLE + LABEL_OUTSIDE_OFFSET_FT), text: '6 ft', rotationDeg: perpCcwRot, color: COLOR_ACTIVE },
        // 14.5 ft just outside the outer circle, 90° CW from the arrow
        // (opposite side from the 6 ft label, clear of the arrow).
        { pos: computeDestinationFeet(isd.insertionPoint, azimuth + 90, FT_OUTER + LABEL_OUTSIDE_OFFSET_FT), text: '14.5 ft', rotationDeg: perpCwRot, color: COLOR_ACTIVE },
      ] : [];

      while (bundle.labels.length > labelSpecs.length) {
        const removed = bundle.labels.pop();
        removed.setMap(null);
      }
      labelSpecs.forEach((spec, i) => {
        const labelColor = spec.color ?? COLOR_ACTIVE;
        if (!bundle.labels[i]) {
          const label = new LabelClass({
            position: spec.pos,
            text: spec.text,
            rotationDeg: spec.rotationDeg,
            color: labelColor,
          });
          label.setMap(map);
          bundle.labels[i] = label;
        } else {
          bundle.labels[i].update({
            position: spec.pos,
            text: spec.text,
            rotationDeg: spec.rotationDeg,
            color: labelColor,
          });
        }
      });
    });

    Object.keys(overlaysRef.current).forEach((id) => {
      if (!live.has(id)) {
        removeBundle(overlaysRef.current[id]);
        delete overlaysRef.current[id];
      }
    });
  }, [map, intersections, activeIntersectionId, selectedTargetId, pathsPanelMode]);

  useEffect(() => () => {
    Object.values(overlaysRef.current).forEach((bundle) => removeBundle(bundle));
    overlaysRef.current = {};
  }, []);

  return overlaysRef;
}

function createBundle(g, map, id, activeIdRef, onSetActiveRef) {
  // Halo layer (black, wider stroke) sits below; main layer sits above.
  // Outer circle is the hit target for hover + click-to-select.
  const innerHalo = new g.Circle({ clickable: false, zIndex: 1 });
  const middleHalo = new g.Circle({ clickable: false, zIndex: 1 });
  const outerHalo = new g.Circle({ clickable: false, zIndex: 1 });
  const arrowHalo = new g.Polyline({ clickable: false, zIndex: 2 });
  const innerCircle = new g.Circle({ clickable: false, zIndex: 3 });
  const middleCircle = new g.Circle({ clickable: false, zIndex: 3 });
  const outerCircle = new g.Circle({ clickable: true, zIndex: 3, cursor: 'pointer' });
  const arrow = new g.Polyline({ clickable: false, zIndex: 4 });
  [innerHalo, middleHalo, outerHalo, arrowHalo,
   innerCircle, middleCircle, outerCircle, arrow].forEach((o) => o.setMap(map));

  const handleIcon = {
    path: g.SymbolPath.CIRCLE,
    scale: 6,
    fillColor: COLOR_ACTIVE,
    fillOpacity: 1,
    strokeColor: COLOR_ACTIVE_HALO,
    strokeWeight: 1.5,
  };
  const centerHandle = new g.Marker({
    map,
    icon: handleIcon,
    draggable: true,
    visible: false,
    zIndex: 10,
    cursor: 'move',
  });
  const tipHandle = new g.Marker({
    map,
    icon: handleIcon,
    draggable: true,
    visible: false,
    zIndex: 10,
    cursor: 'grab',
  });

  const bundle = {
    id,
    innerCircle,
    middleCircle,
    outerCircle,
    arrow,
    innerHalo,
    middleHalo,
    outerHalo,
    arrowHalo,
    labels: [],
    centerHandle,
    tipHandle,
    listeners: [],
  };

  // Hover: raise inactive opacity to hint clickability.
  const setStrokeOpacity = (op) => {
    innerCircle.setOptions({ strokeOpacity: op });
    middleCircle.setOptions({ strokeOpacity: op });
    outerCircle.setOptions({ strokeOpacity: op });
  };

  bundle.listeners.push(g.event.addListener(outerCircle, 'mouseover', () => {
    if (bundle.id === activeIdRef.current) return;
    setStrokeOpacity(STROKE_OPACITY_INACTIVE_HOVER);
  }));
  bundle.listeners.push(g.event.addListener(outerCircle, 'mouseout', () => {
    if (bundle.id === activeIdRef.current) return;
    setStrokeOpacity(bundle.isDimmed ? OPACITY_DIMMED : STROKE_OPACITY_INACTIVE_IDLE);
  }));
  bundle.listeners.push(g.event.addListener(outerCircle, 'click', (e) => {
    if (bundle.id === activeIdRef.current) return;
    onSetActiveRef.current?.(bundle.id);
    if (e?.domEvent?.stopPropagation) e.domEvent.stopPropagation();
  }));

  return bundle;
}

function removeBundle(bundle) {
  if (!bundle) return;
  bundle.innerCircle.setMap(null);
  bundle.middleCircle.setMap(null);
  bundle.outerCircle.setMap(null);
  bundle.arrow.setMap(null);
  bundle.innerHalo?.setMap(null);
  bundle.middleHalo?.setMap(null);
  bundle.outerHalo?.setMap(null);
  bundle.arrowHalo?.setMap(null);
  bundle.labels.forEach((l) => l.setMap(null));
  bundle.centerHandle.setMap(null);
  bundle.tipHandle.setMap(null);
  if (window.google?.maps?.event) {
    bundle.listeners?.forEach((l) => window.google.maps.event.removeListener(l));
  }
}

function inactiveStrokeOpacity(isDimmed) {
  return isDimmed ? OPACITY_DIMMED : STROKE_OPACITY_INACTIVE_IDLE;
}
function circleOptsInner(isActive, isDimmed) {
  return {
    strokeColor: isActive ? COLOR_ACTIVE : COLOR_INNER,
    strokeWeight: STROKE_WEIGHT_MAIN,
    strokeOpacity: isActive ? STROKE_OPACITY_ACTIVE : inactiveStrokeOpacity(isDimmed),
    fillColor: isActive ? COLOR_ACTIVE : COLOR_INNER,
    fillOpacity: isActive
      ? FILL_ALPHA_INNER_ACTIVE
      : (isDimmed ? OPACITY_DIMMED : FILL_ALPHA_INNER_INACTIVE),
    // When active, the inner disc is the move target — clickable + draggable
    // anywhere inside the disc. Bumped zIndex so it wins clicks/drags over
    // the outer ring inside its area.
    clickable: isActive,
    draggable: isActive,
    zIndex: isActive ? 4 : 3,
    cursor: isActive ? 'move' : undefined,
  };
}
function circleOptsMiddle(isActive, isDimmed) {
  return {
    strokeColor: isActive ? COLOR_ACTIVE : COLOR_MIDDLE,
    strokeWeight: STROKE_WEIGHT_MAIN,
    strokeOpacity: isActive ? STROKE_OPACITY_ACTIVE : inactiveStrokeOpacity(isDimmed),
    fillOpacity: 0,
  };
}
function circleOptsOuter(isActive, isDimmed) {
  return {
    strokeColor: isActive ? COLOR_ACTIVE : COLOR_OUTER,
    strokeWeight: STROKE_WEIGHT_MAIN,
    strokeOpacity: isActive ? STROKE_OPACITY_ACTIVE : inactiveStrokeOpacity(isDimmed),
    fillOpacity: 0,
  };
}
function arrowOpts(isActive, isDimmed) {
  const g = window.google?.maps;
  const arrowSymbol = g ? {
    path: g.SymbolPath.FORWARD_CLOSED_ARROW,
    scale: 3.5,
    strokeColor: isActive ? COLOR_ACTIVE_HALO : COLOR_ARROW,
    strokeWeight: isActive ? 1 : 0,
    fillColor: isActive ? COLOR_ACTIVE : COLOR_ARROW,
    fillOpacity: isDimmed ? OPACITY_DIMMED : 1,
  } : null;
  return {
    strokeColor: isActive ? COLOR_ACTIVE : COLOR_ARROW,
    strokeWeight: STROKE_WEIGHT_MAIN,
    strokeOpacity: isActive ? STROKE_OPACITY_ACTIVE : inactiveStrokeOpacity(isDimmed),
    icons: arrowSymbol ? [{ icon: arrowSymbol, offset: '100%' }] : [],
  };
}
/** Black halo that sits underneath an active circle or arrow. */
function haloOpts(isActive) {
  return {
    visible: isActive,
    strokeColor: COLOR_ACTIVE_HALO,
    strokeWeight: STROKE_WEIGHT_HALO,
    strokeOpacity: isActive ? 1 : 0,
    fillOpacity: 0,
  };
}
function arrowHaloOpts(isActive) {
  return {
    visible: isActive,
    strokeColor: COLOR_ACTIVE_HALO,
    strokeWeight: STROKE_WEIGHT_HALO,
    strokeOpacity: isActive ? 1 : 0,
    icons: [],
  };
}
